<template>
    <!-- Row is padding-free on purpose: the desktop menu and the mobile bottom
         sheet space their rows differently, so each call site supplies its own
         padding via the fallthrough class. -->
    <div class="gap-sm flex items-center justify-between" @click.stop>
        <span>Theme</span>
        <TooltipProvider :delay-duration="200">
            <fieldset
                class="theme-toggle gap-2xs p-2xs relative inline-flex items-center rounded-md bg-(--surface-highlight)"
                role="radiogroup"
                aria-label="Theme"
            >
                <legend class="sr-only">Theme</legend>
                <span
                    aria-hidden="true"
                    class="theme-toggle-indicator top-2xs left-2xs pointer-events-none absolute size-8 rounded-sm bg-[var(--bg-lightest)] transition-transform duration-200 ease-out"
                    :style="{ transform: `translateX(${themeIndicatorIndex * OPTION_STRIDE_PX}px)` }"
                />
                <Tooltip v-for="option in themeOptions" :key="option.value">
                    <TooltipTrigger as-child>
                        <label
                            class="theme-toggle-option relative z-10 flex size-8 cursor-pointer items-center justify-center rounded-sm text-[var(--text-primary)] transition-colors"
                            :class="
                                themeStore.mode !== option.value
                                    ? 'hover:bg-(--surface-highlight)/70 has-[:focus-visible]:bg-(--surface-highlight)/70'
                                    : ''
                            "
                            :aria-label="option.label"
                            @click.stop
                        >
                            <input
                                type="radio"
                                name="theme-mode"
                                class="sr-only"
                                :value="option.value"
                                :checked="themeStore.mode === option.value"
                                @change="themeStore.setMode(option.value)"
                            />
                            <component :is="option.icon" class="size-4" />
                        </label>
                    </TooltipTrigger>
                    <TooltipContent>{{ option.tooltip }}</TooltipContent>
                </Tooltip>
            </fieldset>
        </TooltipProvider>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Sun, Moon, Monitor } from 'lucide-vue-next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useThemeStore, type ThemeMode } from '@/stores/theme';

/**
 * Option width (size-8 = 32px) plus the fieldset's gap-2xs (4px).
 * 32px options: 24px read as too small to tap on mobile (MR !312 review).
 */
const OPTION_STRIDE_PX = 36;

const themeStore = useThemeStore();
const themeOptions: { value: ThemeMode; label: string; tooltip: string; icon: typeof Sun }[] = [
    { value: 'system', label: 'System', tooltip: 'Match your operating system', icon: Monitor },
    { value: 'light', label: 'Light', tooltip: 'Light theme', icon: Sun },
    { value: 'dark', label: 'Dark', tooltip: 'Dark theme', icon: Moon },
];
const themeIndicatorIndex = computed(() => themeOptions.findIndex((o) => o.value === themeStore.mode));
</script>
